import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { installedGearQuery, productsQuery } from '../api/inventory.ts';
import { meQuery } from '../api/me.ts';
import {
  addContact,
  type ContactInput,
  type ContactRole,
  contactsQuery,
  KIND_LABELS,
  RELATIONSHIP_LABELS,
  retireContact,
  ROLE_LABELS,
  updateContact,
  updateVenue,
  type Venue,
  type VenueContact,
  type VenueKind,
  type VenueRelationship,
  roomsQuery,
  venueQuery,
  venuesQuery,
} from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { AddButton, EditButton, RemoveButton } from '../components/action-buttons.tsx';
import { Field } from '../components/field.tsx';
import { Popover } from '../components/popover.tsx';
import { AccessSection } from './venue-access.tsx';
import { VenueBilling } from './venue-agreement.tsx';
import { PlansAndPhotos } from './venue-files.tsx';
import { VenueInclusions } from './venue-inclusions.tsx';
import { VenueHeader, VenueProblems } from './venue-header.tsx';
import { VenueOverview } from './venue-overview.tsx';
import { RoomsSection, type RoomsTool } from './venue-rooms.tsx';
import { VenueRules } from './venue-rules.tsx';

// CB-REQ VEN-01 and VEN-07 at v1.971 (venue slice 1, part A): one venue — what kind of place it is,
// how the company relates to it, whether anyone has surveyed it and what is still unknown, where to
// gather in an emergency, and who to call there. From VEN-01 at v1.9104 (the operator's venue page
// design of 2026-10-26), one record in tabs under the same header on every tab, with the problem
// line under it: the panels the slices built move into Overview, Rooms, Access, Equipment, Rules
// and Money (above the price line). History comes with venue slice 5.

export type VenueTab = 'overview' | 'rooms' | 'access' | 'equipment' | 'rules' | 'money';

export function VenueScreen() {
  const { venueId } = useParams({ from: '/authenticated/venues/$venueId' });
  const { tab: chosen } = useSearch({ strict: false }) as { tab?: VenueTab };
  const navigate = useNavigate();
  const venue = useQuery(venueQuery(venueId));
  const me = useQuery(meQuery);
  const capabilities = me.data?.capabilities ?? [];
  const mayManage = capabilities.includes('venues.manage');
  // VEN-11 at v1.988: the agreement's margins are money, seen only with prices.
  const seesPrices = capabilities.includes('prices.view');
  const rooms = useQuery(roomsQuery(venueId));
  const installed = useQuery(installedGearQuery(venueId));
  const products = useQuery({
    ...productsQuery,
    enabled: capabilities.includes('inventory.view'),
  });
  const venueGear = products.data
    ? products.data.filter((p) => p.ownerVenueId === venueId).reduce((n, p) => n + p.stockOnHand, 0)
    : null;
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [roomsTool, setRoomsTool] = useState<RoomsTool>(null);
  const [editingAccess, setEditingAccess] = useState(false);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: venueQuery(venueId).queryKey });
    await queryClient.invalidateQueries({ queryKey: venuesQuery.queryKey });
  };

  if (venue.isPending) return <p className="text-muted">Loading…</p>;
  if (venue.isError) {
    return (
      <p role="alert" className="alert">
        {venue.error.message}
      </p>
    );
  }
  const v = venue.data;
  // Money is above the price line: below it the tab is absent, not disabled (VEN-01 at v1.9104).
  const tabs: Array<[VenueTab, string, number | null]> = [
    ['overview', 'Overview', null],
    ['rooms', 'Rooms', rooms.data?.length ?? null],
    ['access', 'Access', null],
    // VENUE EQUIP (INV-A14): all the venue's own gear, installed in a room or not.
    ['equipment', 'Equipment', venueGear],
    ['rules', 'Rules', null],
    ...(seesPrices ? ([['money', 'Money', null]] as Array<[VenueTab, string, number | null]>) : []),
  ];
  const tab = tabs.some(([key]) => key === chosen) ? chosen! : 'overview';
  const go = (next: VenueTab) =>
    void navigate({
      to: '/venues/$venueId',
      params: { venueId },
      search: next === 'overview' ? {} : { tab: next },
    });

  return (
    <div className="flex flex-col gap-4">
      <Link to="/venues" className="link text-sm">
        ← Venues
      </Link>
      <VenueHeader
        venue={v}
        capabilities={capabilities}
        tenantZone={me.data?.tenant.defaultTz ?? 'Australia/Melbourne'}
        onEdit={mayManage ? () => setEditing(true) : null}
        onChanged={refresh}
      />
      <VenueProblems
        venue={v}
        capabilities={capabilities}
        onChanged={refresh}
        survey={<Survey venue={v} mayManage={mayManage} onChanged={refresh} />}
      />
      {/* Each tab's own tools sit at the right of the tab bar, outside the tab list. */}
      <div className="tabs-bar">
        <div className="tabs" role="tablist" aria-label="Venue">
          {tabs.map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              role="tab"
              id={`venue-tab-${key}`}
              aria-selected={tab === key}
              aria-controls="venue-panel"
              className="tab"
              onClick={() => go(key)}
            >
              {label}
              {count !== null ? <span className="tab-count">{count}</span> : null}
            </button>
          ))}
        </div>
        {tab === 'access' && mayManage ? (
          <div className="tab-tools">
            <Button variant="quiet" className="small-btn" onClick={() => setEditingAccess(true)}>
              Edit access
            </Button>
          </div>
        ) : null}
        {tab === 'rooms' && mayManage ? (
          <div className="tab-tools">
            <Button variant="quiet" className="small-btn" onClick={() => setRoomsTool('add')}>
              + Room
            </Button>
            <Button variant="quiet" className="small-btn" onClick={() => setRoomsTool('combine')}>
              Combine…
            </Button>
          </div>
        ) : null}
      </div>
      <div
        id="venue-panel"
        role="tabpanel"
        aria-labelledby={`venue-tab-${tab}`}
        className="flex flex-col gap-6"
      >
        {tab === 'overview' ? (
          <VenueOverview
            venue={v}
            capabilities={capabilities}
            tenantZone={me.data?.tenant.defaultTz ?? 'Australia/Melbourne'}
            contacts={<Contacts venueId={v.id} mayManage={mayManage} onChanged={refresh} />}
          />
        ) : null}
        {tab === 'rooms' ? (
          <>
            {/* VEN-04 at v1.976: each room in depth, and how rooms combine. */}
            <RoomsSection
              venueId={v.id}
              mayManage={mayManage}
              tool={roomsTool}
              onToolDone={() => setRoomsTool(null)}
            />
            {/* VEN-04 and VEN-08 at v1.976: floor and rigging plans, and photos. */}
            <PlansAndPhotos venueId={v.id} mayManage={mayManage} />
          </>
        ) : null}
        {tab === 'access' ? (
          <AccessSection
            venue={v}
            mayManage={mayManage}
            onChanged={refresh}
            editing={editingAccess}
            onEditDone={() => setEditingAccess(false)}
            capabilities={capabilities}
            tenantZone={me.data?.tenant.defaultTz ?? 'Australia/Melbourne'}
          />
        ) : null}
        {tab === 'equipment' ? (
          <>
            <Installed rows={installed.data ?? []} rooms={rooms.data ?? []} />
            <VenueInclusions venueId={v.id} mayManage={mayManage} />
          </>
        ) : null}
        {tab === 'rules' ? (
          <VenueRules venue={v} capabilities={capabilities} onChanged={refresh} />
        ) : null}
        {tab === 'money' ? (
          /* VEN-11 at v1.988: who the venue is invoiced as, and its agreement. */
          <VenueBilling venue={v} capabilities={capabilities} zone={me.data?.tenant.defaultTz} />
        ) : null}
      </div>
      {editing ? (
        <Popover title={`Edit ${v.name}`} onClose={() => setEditing(false)}>
          <DetailsForm venue={v} onDone={() => setEditing(false)} onChanged={refresh} />
        </Popover>
      ) : null}
    </div>
  );
}

/** VEN-03 at v1.986: the venue's own gear installed in each of its rooms. */
function Installed({
  rows,
  rooms,
}: {
  rows: ReadonlyArray<{
    roomId: string;
    productName: string;
    tag?: string | null;
    quantity: number;
  }>;
  rooms: ReadonlyArray<{ id: string; name: string }>;
}) {
  if (rows.length === 0) return null;
  const roomName = (id: string) => rooms.find((r) => r.id === id)?.name ?? 'A room';
  return (
    <section aria-labelledby="installed" className="flex flex-col gap-3">
      <h2 id="installed" className="section-title">
        Installed in its rooms
      </h2>
      <table className="data-table text-sm">
        <thead>
          <tr>
            <th scope="col">Room</th>
            <th scope="col">Gear</th>
            <th scope="col">Quantity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{roomName(r.roomId)}</td>
              <td>
                {r.productName}
                {r.tag ? ` · ${r.tag}` : ''}
              </td>
              <td>{r.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

const CHECKLIST: Array<[keyof Venue['checklist'], string]> = [
  ['address', 'An address'],
  ['access', 'How to get in'],
  ['contact', 'Someone to call there'],
  ['emergencyContact', 'An emergency contact'],
  ['musterPoint', 'A muster point'],
  ['instructions', 'Its rules, as standing instructions'],
];

/**
 * VEN-01 at v1.971: shown until someone has been there and recorded it — in the problem line from
 * v1.9104. Once surveyed, the header says so; unmarking it is in Edit.
 */
function Survey({
  venue,
  mayManage,
  onChanged,
}: {
  venue: Venue;
  mayManage: boolean;
  onChanged: () => Promise<void>;
}) {
  const mark = useMutation({
    mutationFn: (surveyed: boolean) => updateVenue(venue.id, { surveyed }),
    onSuccess: onChanged,
  });
  const missing = CHECKLIST.filter(([key]) => !venue.checklist[key]).map(([, text]) => text);
  return (
    <div className="problem-line">
      <span className="flex-1">
        <strong id="survey">Not surveyed yet</strong>
        {missing.length > 0 ? ` · still to find: ${missing.join(', ').toLowerCase()}` : ''}
        {mark.error ? (
          <span role="alert" className="block font-semibold">
            {mark.error.message}
          </span>
        ) : null}
      </span>
      {mayManage ? (
        <Button
          variant="quiet"
          className="small-btn bg-white!"
          disabled={mark.isPending}
          onClick={() => mark.mutate(true)}
        >
          Surveyed ✓
        </Button>
      ) : null}
    </div>
  );
}

function DetailsForm({
  venue,
  onDone,
  onChanged,
}: {
  venue: Venue;
  onDone: () => void;
  onChanged: () => Promise<void>;
}) {
  const [name, setName] = useState(venue.name);
  const [holdsStock, setHoldsStock] = useState(venue.holdsStock);
  const [inductionName, setInductionName] = useState(venue.induction?.name ?? '');
  const [inductionMonths, setInductionMonths] = useState(
    venue.induction ? String(venue.induction.validMonths) : '',
  );
  const [surveyed, setSurveyed] = useState(!!venue.surveyed);
  const [problem, setProblem] = useState<string | undefined>();
  const [kind, setKind] = useState<VenueKind>(venue.kind);
  const [relationship, setRelationship] = useState<VenueRelationship>(venue.relationship);
  const [address, setAddress] = useState(venue.address ?? '');
  const [musterPoint, setMusterPoint] = useState(venue.musterPoint ?? '');
  const [listed, setListed] = useState(venue.listed);
  const [timeZone, setTimeZone] = useState(venue.timeZone ?? '');
  const [notes, setNotes] = useState(venue.notes ?? '');
  const save = useMutation({
    mutationFn: () =>
      updateVenue(venue.id, {
        name: name.trim(),
        holdsStock,
        induction: inductionName.trim()
          ? { name: inductionName.trim(), validMonths: Number(inductionMonths) }
          : null,
        ...(surveyed === !!venue.surveyed ? {} : { surveyed }),
        kind,
        relationship,
        address: address.trim() || null,
        musterPoint: musterPoint.trim() || null,
        timeZone: timeZone.trim() || null,
        // Unchanged stays the kind's default; a change is said out loud.
        ...(listed === venue.listed ? {} : { listed }),
        notes: notes.trim() || null,
      }),
    onSuccess: async () => {
      await onChanged();
      onDone();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return setProblem('Give it a name.');
    const months = Number(inductionMonths);
    if (inductionName.trim() && !(Number.isInteger(months) && months >= 1 && months <= 120)) {
      return setProblem('Say how many months the induction lasts (1 to 120).');
    }
    setProblem(undefined);
    save.mutate();
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" aria-label="Venue">
      <Field label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="venue-kind" className="text-xs text-ink-soft">
            Kind
          </label>
          <select
            id="venue-kind"
            className="input"
            value={kind}
            onChange={(e) => setKind(e.currentTarget.value as VenueKind)}
          >
            {(Object.keys(KIND_LABELS) as VenueKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="venue-relationship" className="text-xs text-ink-soft">
            Your relationship
          </label>
          <select
            id="venue-relationship"
            className="input"
            value={relationship}
            onChange={(e) => setRelationship(e.currentTarget.value as VenueRelationship)}
          >
            {(Object.keys(RELATIONSHIP_LABELS) as VenueRelationship[]).map((r) => (
              <option key={r} value={r}>
                {RELATIONSHIP_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Field label="Address" value={address} onChange={(e) => setAddress(e.currentTarget.value)} />
      <Field
        label="Muster point"
        hint="Where everyone gathers in an emergency. Crew see it on their shifts."
        value={musterPoint}
        onChange={(e) => setMusterPoint(e.currentTarget.value)}
      />
      <Field
        label="Time zone"
        hint="Only for a venue outside the company’s own zone, as a place: Australia/Perth, not AWST."
        value={timeZone}
        onChange={(e) => setTimeZone(e.currentTarget.value)}
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={listed}
          onChange={(e) => setListed(e.currentTarget.checked)}
        />
        Listed
      </label>
      <Field label="Notes" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
      {/* INV-A12: our stock kept there; CMP-05 / VEN-05: the induction crew need. */}
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={holdsStock}
          onChange={(e) => setHoldsStock(e.currentTarget.checked)}
        />
        Our stock is kept here
      </label>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_10rem]">
        <Field
          label="Induction"
          hint="The venue's own, if crew need one to work here."
          value={inductionName}
          onChange={(e) => setInductionName(e.currentTarget.value)}
        />
        <Field
          label="Lasts (months)"
          inputMode="numeric"
          value={inductionMonths}
          onChange={(e) => setInductionMonths(e.currentTarget.value)}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={surveyed}
          onChange={(e) => setSurveyed(e.currentTarget.checked)}
        />
        Surveyed
      </label>
      {problem ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem}
        </p>
      ) : null}
      {save.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {save.error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** VEN-07 at v1.971: the people to call at the venue. */
function Contacts({
  venueId,
  mayManage,
  onChanged,
}: {
  venueId: string;
  mayManage: boolean;
  onChanged: () => Promise<void>;
}) {
  const contacts = useQuery(contactsQuery(venueId));
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: contactsQuery(venueId).queryKey });
    await onChanged();
  };
  return (
    <section aria-labelledby="contacts" className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <h2 id="contacts" className="section-title">
          Contacts
        </h2>
        {mayManage && !adding ? (
          <AddButton noun="Contact" variant="quiet" onClick={() => setAdding(true)} />
        ) : null}
      </div>
      {adding ? (
        <ContactForm venueId={venueId} onDone={() => setAdding(false)} onChanged={refresh} />
      ) : null}
      {contacts.isError ? (
        <p role="alert" className="alert">
          {contacts.error.message}
        </p>
      ) : null}
      {contacts.data?.length === 0 && !adding ? (
        <p className="text-sm text-ink-soft">Nobody recorded yet.</p>
      ) : null}
      <ul className="flex flex-col gap-3">
        {contacts.data?.map((c) => (
          <ContactRow
            key={c.id}
            venueId={venueId}
            contact={c}
            mayManage={mayManage}
            onChanged={refresh}
          />
        ))}
      </ul>
    </section>
  );
}

function ContactRow({
  venueId,
  contact,
  mayManage,
  onChanged,
}: {
  venueId: string;
  contact: VenueContact;
  mayManage: boolean;
  onChanged: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const retire = useMutation({
    mutationFn: () => retireContact(venueId, contact.id),
    onSuccess: onChanged,
  });
  if (editing) {
    return (
      <li>
        <ContactForm
          venueId={venueId}
          contact={contact}
          onDone={() => setEditing(false)}
          onChanged={onChanged}
        />
      </li>
    );
  }
  return (
    <li className="flex flex-col gap-1 rounded-md bg-tile p-3 text-sm">
      <span className="font-semibold">{contact.name}</span>
      <span className="flex flex-wrap gap-1">
        {contact.roles.map((r) => (
          <span key={r} className="tag">
            {ROLE_LABELS[r]}
          </span>
        ))}
      </span>
      {contact.phone ? <span>{contact.phone}</span> : null}
      {contact.email ? <span>{contact.email}</span> : null}
      {contact.notes ? <span className="text-ink-soft">{contact.notes}</span> : null}
      {retire.error ? (
        <p role="alert" className="font-semibold text-accent-2-700">
          {retire.error.message}
        </p>
      ) : null}
      {mayManage ? (
        <span className="flex gap-2">
          {removing ? (
            <>
              <span>Remove {contact.name}?</span>
              <Button variant="quiet" disabled={retire.isPending} onClick={() => retire.mutate()}>
                Yes, remove
              </Button>
              <Button variant="quiet" onClick={() => setRemoving(false)}>
                Keep
              </Button>
            </>
          ) : (
            <>
              <EditButton what={contact.name} onClick={() => setEditing(true)} />
              <RemoveButton what={contact.name} onClick={() => setRemoving(true)} />
            </>
          )}
        </span>
      ) : null}
    </li>
  );
}

function ContactForm({
  venueId,
  contact,
  onDone,
  onChanged,
}: {
  venueId: string;
  contact?: VenueContact;
  onDone: () => void;
  onChanged: () => Promise<void>;
}) {
  const [name, setName] = useState(contact?.name ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [notes, setNotes] = useState(contact?.notes ?? '');
  const [roles, setRoles] = useState<ContactRole[]>(contact ? [...contact.roles] : []);
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (input: ContactInput) =>
      contact ? updateContact(venueId, contact.id, input) : addContact(venueId, input),
    onSuccess: async () => {
      await onChanged();
      onDone();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return setProblem('Give the contact a name.');
    if (roles.length === 0) return setProblem('Tick at least one role.');
    setProblem(undefined);
    save.mutate({
      name: name.trim(),
      phone: phone.trim() || null,
      email: email.trim() || null,
      notes: notes.trim() || null,
      roles,
    });
  }
  return (
    <form onSubmit={submit} className="flex max-w-2xl flex-col gap-4 border-2 border-divider p-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <Field
          label="Phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
        />
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <Field label="Notes" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs text-ink-soft">Roles</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {(Object.keys(ROLE_LABELS) as ContactRole[]).map((r) => (
            <label key={r} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={roles.includes(r)}
                onChange={(e) => {
                  const on = e.currentTarget.checked;
                  setRoles((now) => (on ? [...now, r] : now.filter((x) => x !== r)));
                }}
              />
              {ROLE_LABELS[r]}
            </label>
          ))}
        </div>
      </fieldset>
      {problem || save.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          {contact ? 'Save' : 'Add contact'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
